import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { TenantApiError, type TenantInvitationInspect } from "@qr-menu/api-tenant";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
} from "@qr-menu/ui";
import { tenantApi } from "../lib/api";
import {
  MAX_PASSWORD_BYTES,
  isBlank,
  runeLength,
  utf8ByteLength,
} from "../lib/passwordValidation";

const REQUIRED_FIELDS_MESSAGE = "Ad, soyad, parola ve tekrarı zorunludur.";
const MISMATCH_MESSAGE = "Parolalar eşleşmiyor.";
const TOO_SHORT_MESSAGE = "Parola en az 8 karakter olmalıdır.";
const TOO_LONG_MESSAGE = "Parola çok uzun.";
const GENERIC_FAILURE_MESSAGE =
  "Şu anda işlem gerçekleştirilemiyor. Lütfen daha sonra tekrar deneyin.";
const ACCEPT_SUCCESS_MESSAGE =
  "Daveti kabul ettiniz. Hesabınızla giriş yapabilirsiniz.";

const ROLE_LABELS: Record<string, string> = {
  owner: "Sahip",
  admin: "Yönetici",
  editor: "Editör",
};

function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

// A loose sanity check only — length and URL-safe base64 charset, mirroring
// ResetPasswordRoute's isPlausibleToken. The backend
// (InvitationService.AcceptInvitation / parseCanonicalInvitationToken) is the
// sole authority on the exact canonical format (43 chars, strict Raw URL
// base64, 32 decoded bytes, canonical re-encoding).
const TOKEN_CHARSET_PATTERN = /^[A-Za-z0-9_-]+$/;
const MIN_PLAUSIBLE_TOKEN_LENGTH = 20;

function isPlausibleToken(value: string): boolean {
  return (
    value.length >= MIN_PLAUSIBLE_TOKEN_LENGTH &&
    TOKEN_CHARSET_PATTERN.test(value)
  );
}

// Reads only the URL fragment (window.location.hash), never the query
// string: the invitation link is <origin>/accept-invitation#token=<token>,
// and the token must never appear in a query parameter, which browsers and
// proxies commonly log. Manual parsing on purpose — no
// URLSearchParams/useSearchParams on location.search, since that would read
// the wrong part of the URL. Identical to ResetPasswordRoute's own parser.
function extractTokenFromFragment(hash: string): string | null {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return null;

  for (const pair of raw.split("&")) {
    const [key, value] = pair.split("=");
    if (key === "token" && value) {
      try {
        return decodeURIComponent(value);
      } catch {
        return null;
      }
    }
  }
  return null;
}

type InspectState =
  | { kind: "loading" }
  | { kind: "invalid" }
  | { kind: "ready"; data: TenantInvitationInspect };

export function AcceptInvitationRoute() {
  // Read once, synchronously, during the first render — directly from
  // window.location.hash. This is the ONLY place the token is ever read from
  // the URL. From here on it lives only in this component's state: never in
  // router/navigation state, a global store, localStorage, sessionStorage, a
  // cookie, the tenant session module, a log line, or an error message. The
  // invitation capability is never carried through /login via router state
  // either — only a one-off success message string, the same as
  // ResetPasswordRoute.
  const [token] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    const extracted = extractTokenFromFragment(window.location.hash);
    return extracted && isPlausibleToken(extracted) ? extracted : null;
  });

  // Strips the fragment from the visible address bar before the browser
  // paints, so the raw token spends as little time as possible visible in
  // the URL bar, browser history, or anywhere a screenshot/share could catch
  // it. Guarded to run only once: React 18 StrictMode double-invokes effects
  // in development, and a second replaceState call would otherwise be a
  // harmless but pointless no-op.
  //
  // Passes the CURRENT history.state through rather than null: react-router's
  // BrowserRouter already stamps window.history.state with its own
  // { idx, usr, key } bookkeeping while mounting, before this component's
  // effects run. Passing null here would silently corrupt that bookkeeping.
  // Only the URL changes; the state object is carried through unmodified.
  const strippedRef = useRef(false);
  useLayoutEffect(() => {
    if (strippedRef.current) return;
    strippedRef.current = true;
    if (typeof window === "undefined") return;
    if (window.location.hash) {
      const url = window.location.pathname + window.location.search;
      window.history.replaceState(window.history.state, "", url);
    }
  }, []);

  const [inspectState, setInspectState] = useState<InspectState>({
    kind: "loading",
  });

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    tenantApi
      .inspectInvitation({ token })
      .then((data) => {
        if (!cancelled) setInspectState({ kind: "ready", data });
      })
      .catch(() => {
        // Every invalid-token reason (unknown, malformed, expired, revoked,
        // already accepted) collapses to the same generic state — the
        // backend never distinguishes them and neither does this page.
        if (!cancelled) setInspectState({ kind: "invalid" });
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting || !token || inspectState.kind !== "ready") return;

    const accountExists = inspectState.data.account_exists;

    if (!accountExists) {
      if (isBlank(name) || isBlank(surname) || !newPassword || !confirmPassword) {
        setError(REQUIRED_FIELDS_MESSAGE);
        return;
      }
      if (newPassword !== confirmPassword) {
        setError(MISMATCH_MESSAGE);
        return;
      }
      if (runeLength(newPassword) < 8) {
        setError(TOO_SHORT_MESSAGE);
        return;
      }
      if (utf8ByteLength(newPassword) > MAX_PASSWORD_BYTES) {
        setError(TOO_LONG_MESSAGE);
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    try {
      await tenantApi.acceptInvitation(
        accountExists
          ? { token }
          : {
              token,
              name: name.trim(),
              surname: surname.trim(),
              password: newPassword,
            },
      );
      setNewPassword("");
      setConfirmPassword("");
      // No auto-login and nothing written to the tenant session store: the
      // caller signs in fresh, existing account or brand new one alike —
      // AcceptInvitation never issues a JWT.
      navigate("/login", {
        replace: true,
        state: { message: ACCEPT_SUCCESS_MESSAGE },
      });
    } catch (err) {
      if (err instanceof TenantApiError && err.status === 409) {
        // The expected new-account race: an account was created for this
        // email between inspect and accept. Re-inspect so the page can
        // switch to the existing-account acceptance flow.
        try {
          const fresh = await tenantApi.inspectInvitation({ token });
          setInspectState({ kind: "ready", data: fresh });
          setError(
            "Bu e-posta için az önce bir hesap oluşturuldu. Lütfen tekrar deneyin.",
          );
        } catch {
          setInspectState({ kind: "invalid" });
        }
        return;
      }
      if (err instanceof TenantApiError && err.status === 422) {
        setInspectState({ kind: "invalid" });
        return;
      }
      setError(GENERIC_FAILURE_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <div className="auth-shell">
        <Card className="auth-card">
          <CardHeader>
            <CardTitle>Ekip daveti</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="state error">
              Bu davet bağlantısı bu oturumda kullanılamıyor.
              <br />
              E-postadaki bağlantıyı tekrar açın.
            </div>
            <p className="hint">
              <Link to="/login">Girişe dön</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (inspectState.kind === "loading") {
    return (
      <div className="auth-shell">
        <Card className="auth-card">
          <CardHeader>
            <CardTitle>Ekip daveti</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="state">Davet kontrol ediliyor…</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (inspectState.kind === "invalid") {
    return (
      <div className="auth-shell">
        <Card className="auth-card">
          <CardHeader>
            <CardTitle>Ekip daveti</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="state error">
              Bu davet bağlantısı geçersiz, süresi dolmuş veya zaten
              kullanılmış.
              <br />
              Ekip yöneticinizden yeni bir davet isteyin.
            </div>
            <p className="hint">
              <Link to="/login">Girişe dön</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { business_name, role, account_exists } = inspectState.data;

  return (
    <div className="auth-shell">
      <Card className="auth-card">
        <CardHeader>
          <CardTitle>Ekip daveti</CardTitle>
          <CardDescription>
            <strong>{business_name}</strong> işletmesi sizi{" "}
            <strong>{roleLabel(role)}</strong> rolüyle ekibine davet etti.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="login-form" onSubmit={handleSubmit}>
            {!account_exists && (
              <>
                <Field label="Ad" htmlFor="accept-invitation-first-name">
                  <input
                    id="accept-invitation-first-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoComplete="given-name"
                    required
                  />
                </Field>
                <Field label="Soyad" htmlFor="accept-invitation-last-name">
                  <input
                    id="accept-invitation-last-name"
                    type="text"
                    value={surname}
                    onChange={(e) => setSurname(e.target.value)}
                    autoComplete="family-name"
                    required
                  />
                </Field>
                <Field label="Parola" htmlFor="accept-invitation-password">
                  <input
                    id="accept-invitation-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    spellCheck={false}
                    required
                  />
                </Field>
                <Field
                  label="Parola (tekrar)"
                  htmlFor="accept-invitation-confirm-password"
                >
                  <input
                    id="accept-invitation-confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    spellCheck={false}
                    required
                  />
                </Field>
              </>
            )}
            {error && <div className="state error">{error}</div>}
            <div className="login-actions">
              <button type="submit" className="primary" disabled={submitting}>
                {submitting
                  ? "Gönderiliyor…"
                  : account_exists
                    ? "Daveti kabul et"
                    : "Hesabımı oluştur ve daveti kabul et"}
              </button>
            </div>
          </form>
          {account_exists && (
            <p className="hint">
              Bu e-posta için zaten bir hesabınız var. Daveti kabul ettikten
              sonra mevcut parolanızla giriş yapabilirsiniz.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
